import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Layers, Send, MessageCircle, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BundleRemindersPage({ embedded = false }) {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const [s, c] = await Promise.all([
        axios.get(`${API}/bundle-reminders/settings`, auth()),
        axios.get(`${API}/bundle-reminders/candidates`, auth()),
      ]);
      setSettings(s.data);
      setCandidates(c.data.candidates || []);
    } catch (e) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const patch = async (update) => {
    try {
      const { data } = await axios.put(`${API}/bundle-reminders/settings`, update, auth());
      setSettings((prev) => ({ ...prev, ...data }));
      toast.success('Gespeichert');
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
    }
  };

  const createWaTemplate = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/bundle-reminders/whatsapp-template/create`, {}, auth());
      if (data.success) toast.success(`WhatsApp-Vorlage eingereicht (Status: ${data.status || 'PENDING'})`);
      else toast.error(`Fehler: ${JSON.stringify(data.error).slice(0, 180)}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Einreichen fehlgeschlagen');
    } finally { setBusy(false); }
  };

  const sendOne = async (cid, name) => {
    if (!window.confirm(`Sammel-Zahlungsaufforderung jetzt an ${name} senden?`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/bundle-reminders/send/${cid}`, {}, auth());
      toast.success(`Gesendet an ${name} — E-Mail: ${data.channels?.email || '–'}, WhatsApp: ${data.channels?.whatsapp || '–'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen');
    } finally { setBusy(false); }
  };

  const runNow = async () => {
    if (!window.confirm('Wochen-Lauf jetzt für ALLE Kandidaten starten?')) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/bundle-reminders/run-now`, {}, auth());
      toast.success(`Lauf fertig: ${data.sent || 0} gesendet, ${data.skipped || 0} übersprungen, ${data.failed || 0} Fehler`);
      await load();
    } catch (e) {
      toast.error('Lauf fehlgeschlagen');
    } finally { setBusy(false); }
  };

  const chf = (n) => `CHF ${Number(n || 0).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/,/g, "'")}`;

  if (loading) return <div className="p-8 text-gray-500">Lädt...</div>;

  const waStatus = settings?.whatsapp_template_status || 'NOT_CREATED';
  const waApproved = waStatus === 'APPROVED';

  return (
    <div className="p-8 max-w-3xl" data-testid="bundle-reminders-page">
      {!embedded && (
        <Button variant="ghost" size="sm" className="mb-4" onClick={() => navigate('/settings')} data-testid="bundle-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Zurück zu Einstellungen
        </Button>
      )}

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Layers className="h-6 w-6 text-cyan-600" /> Sammel-Zahlungsaufforderung
        </h1>
        <p className="text-gray-600 mt-1">
          Kunden mit mehreren offenen Trainings bekommen EINE Nachricht mit Sammel-Zahllink und der
          Option, Karte/TWINT zu hinterlegen (dann werden alle offenen Beträge automatisch abgebucht).
        </p>
      </div>

      {/* Automatik */}
      <Card className="mb-6">
        <CardHeader><CardTitle className="text-base">Automatischer Wochen-Versand</CardTitle>
          <CardDescription>Dienstags 09:00 an alle Kunden mit genügend offenen Rechnungen.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Automatik aktiv</span>
            <Switch checked={!!settings?.enabled} onCheckedChange={(v) => patch({ enabled: v })} data-testid="bundle-enabled-toggle" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">E-Mail senden</span>
            <Switch checked={!!settings?.send_email} onCheckedChange={(v) => patch({ send_email: v })} data-testid="bundle-email-toggle" />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">WhatsApp senden</span>
            <Switch checked={!!settings?.send_whatsapp} onCheckedChange={(v) => patch({ send_whatsapp: v })} data-testid="bundle-whatsapp-toggle" />
          </div>
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="text-xs text-gray-500">Ab wie vielen offenen Rechnungen</label>
              <Input type="number" min={2} className="mt-1 w-28" defaultValue={settings?.min_open_invoices}
                onBlur={(e) => patch({ min_open_invoices: parseInt(e.target.value || '2', 10) })}
                data-testid="bundle-min-input" />
            </div>
            <div>
              <label className="text-xs text-gray-500">Cooldown (Tage)</label>
              <Input type="number" min={1} className="mt-1 w-28" defaultValue={settings?.cooldown_days}
                onBlur={(e) => patch({ cooldown_days: parseInt(e.target.value || '7', 10) })}
                data-testid="bundle-cooldown-input" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* WhatsApp-Vorlage */}
      <Card className="mb-6">
        <CardHeader><CardTitle className="text-base flex items-center gap-2">
          <MessageCircle className="h-4 w-4" /> WhatsApp-Vorlage
          <Badge className={waApproved ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'} data-testid="bundle-wa-status">
            {waStatus}
          </Badge>
        </CardTitle>
          <CardDescription>WhatsApp braucht eine von Meta freigegebene Vorlage. Einmalig einreichen — nach Freigabe sendet die Automatik automatisch per WhatsApp.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={createWaTemplate} disabled={busy} data-testid="bundle-create-wa-btn">
            {busy ? 'Bitte warten…' : (waStatus === 'NOT_CREATED' ? 'WhatsApp-Vorlage einreichen' : 'Vorlage erneut einreichen')}
          </Button>
          {!waApproved && (
            <p className="mt-2 text-xs text-gray-500">Bis zur Freigabe wird nur per E-Mail versendet.</p>
          )}
        </CardContent>
      </Card>

      {/* Kandidaten */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base">Offene Kandidaten ({candidates.length})</CardTitle>
            <CardDescription>Kunden mit mehreren offenen Trainings-Rechnungen.</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={load} data-testid="bundle-refresh-btn"><RefreshCw className="h-4 w-4" /></Button>
            <Button size="sm" onClick={runNow} disabled={busy || candidates.length === 0} data-testid="bundle-run-now-btn">
              <Send className="h-4 w-4 mr-1" /> Jetzt alle senden
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {candidates.length === 0 ? (
            <p className="text-sm text-gray-500">Aktuell keine Kunden mit mehreren offenen Rechnungen.</p>
          ) : (
            <div className="divide-y">
              {candidates.map((c) => (
                <div key={c.customer_id} className="flex items-center justify-between py-3" data-testid={`bundle-candidate-${c.customer_id}`}>
                  <div>
                    <div className="font-medium text-gray-900">{c.name || c.customer_id}</div>
                    <div className="text-xs text-gray-500">
                      {c.count} offene Rechnungen · {chf(c.total)}
                      {!c.email && <span className="text-amber-600"> · keine E-Mail</span>}
                      {!c.phone && <span className="text-amber-600"> · keine Nummer</span>}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => sendOne(c.customer_id, c.name || 'Kunde')} disabled={busy}
                    data-testid={`bundle-send-${c.customer_id}`}>
                    <Send className="h-4 w-4 mr-1" /> Senden
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
